import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useOpsRavenAccountCheck,
  useOpsRavenOrgs,
  useOpsRebindRavenCredential,
} from '@/api/operations-org-hooks';
import type { OpsServiceRow } from '@/api/operations';

type Source = 'provision' | 'service' | 'manual';

/**
 * Default console login, mirroring controlplane's supportAliasEmail pattern
 * ("support+<local>-<orgID>@vnetwork.vn") with the "cdn" base the ops
 * provisioning path uses. Self-activation uses the activator's own local-part.
 */
const ravenAdminAlias = (orgId: string) => `support+cdn-${orgId}@vnetwork.vn`;

/** Radix Select can't hold an empty value, so "no vEdge org chosen" uses a sentinel. */
const KEEP_UID = '__keep__';

interface Props {
  service: OpsServiceRow;
  /** Every Raven service of the same org (including `service`). */
  ravenServices: OpsServiceRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Debounce a fast-changing string (typing) before it drives a request. */
function useDebounced(value: string, ms = 400): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Re-bind the credential of a Raven service. Raven credentials are shared at org
 * level, so the dialog defaults to applying the change to every Raven service of
 * the org. Three credential sources:
 *  - provision: check the email on vEdge, create the login (org_admin of the chosen
 *    vEdge org) or reset its password, and store the generated password — nothing
 *    to create by hand or retype;
 *  - service: copy another Raven service's stored credential;
 *  - manual: type a login created by hand on the vncdn.vn console.
 */
export function OpsRavenRebindDialog({ service, ravenServices, open, onOpenChange }: Props) {
  const rebind = useOpsRebindRavenCredential();
  const siblings = ravenServices.filter((s) => s.id !== service.id);
  const defaultEmail = ravenAdminAlias(service.organization_id);

  const [source, setSource] = useState<Source>('provision');
  const [email, setEmail] = useState(defaultEmail);
  const [fromId, setFromId] = useState('');
  const [username, setUsername] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [applyToOrg, setApplyToOrg] = useState(true);
  const [orgSearch, setOrgSearch] = useState(service.organization_id);
  const [vedgeOrgId, setVedgeOrgId] = useState(KEEP_UID);
  const [reason, setReason] = useState('');

  const orgQuery = useDebounced(orgSearch.trim());
  const checkedEmail = useDebounced(email.trim());
  const vedgeOrgs = useOpsRavenOrgs(orgQuery, open);
  const account = useOpsRavenAccountCheck(checkedEmail, open && source === 'provision');
  const acc = account.data;

  // An existing login pins its vEdge org: preselect it so the operator can't
  // pick a mismatching one by accident.
  useEffect(() => {
    if (source === 'provision' && acc?.exists && acc.org_id) {
      setVedgeOrgId(String(acc.org_id));
    }
  }, [source, acc?.exists, acc?.org_id]);

  const orgOptions = (vedgeOrgs.data ?? []).map((o) => ({
    id: String(o.id),
    label: `${o.id} — ${o.name} (${o.domain_count} domains)`,
  }));
  if (vedgeOrgId !== KEEP_UID && !orgOptions.some((o) => o.id === vedgeOrgId)) {
    orgOptions.unshift({
      id: vedgeOrgId,
      label: `${vedgeOrgId}${acc?.org_name && String(acc.org_id) === vedgeOrgId ? ` — ${acc.org_name}` : ''}`,
    });
  }

  const orgCount = ravenServices.filter((s) => s.status !== 'terminated').length;
  const orgConflict =
    source === 'provision' &&
    !!acc?.exists &&
    vedgeOrgId !== KEEP_UID &&
    String(acc.org_id) !== vedgeOrgId;
  const checkPending =
    source === 'provision' && (account.isFetching || checkedEmail !== email.trim());

  const credentialOk =
    source === 'service'
      ? fromId !== ''
      : source === 'manual'
        ? username.trim() !== '' && password !== ''
        : email.trim() !== '' &&
          vedgeOrgId !== KEEP_UID &&
          !account.isError &&
          !!acc &&
          !orgConflict &&
          !checkPending;
  const canSubmit = credentialOk && reason.trim() !== '' && !rebind.isPending;

  const handleClose = () => {
    if (rebind.isPending) return;
    onOpenChange(false);
    setFromId('');
    setEmail(defaultEmail);
    setUsername(defaultEmail);
    setPassword('');
    setReason('');
    setVedgeOrgId(KEEP_UID);
    rebind.reset();
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    try {
      await rebind.mutateAsync({
        serviceId: service.id,
        reason: reason.trim(),
        apply_to_org: applyToOrg,
        ...(vedgeOrgId !== KEEP_UID ? { vedge_org_id: vedgeOrgId } : {}),
        ...(source === 'provision'
          ? { provision: true, email: email.trim() }
          : source === 'service'
            ? { from_service_id: fromId }
            : { username: username.trim(), password }),
      });
      handleClose();
    } catch {
      // Error stays visible via rebind.isError; dialog stays open.
    }
  };

  const errStatus = (rebind.error as { response?: { status?: number; data?: { error_message?: string } } } | null)
    ?.response;

  const orgPicker = (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <Field
        label={
          source === 'provision' ? 'vEdge organization (required)' : 'vEdge organization (optional)'
        }
      >
        {(p) => (
          <Input
            {...p}
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            placeholder="Search by id, name or slug"
          />
        )}
      </Field>
      <Select value={vedgeOrgId} onValueChange={setVedgeOrgId}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={KEEP_UID}>
            {source === 'provision'
              ? 'Select a vEdge organization'
              : `Keep current${service.entity_uids[0] ? ` (${service.entity_uids[0]})` : ''}`}
          </SelectItem>
          {orgOptions.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {vedgeOrgs.isError && <Alert tone="warning">Could not load vEdge organizations.</Alert>}
      {vedgeOrgId !== KEEP_UID && (
        <span style={{ color: 'var(--vn-fg-muted)' }}>
          Only {service.service_name} is re-pointed to this vEdge org; the credential change
          still follows the checkbox below.
        </span>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Re-bind credential</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', fontSize: 'var(--vn-font-size-sm)' }}>
            <div>
              Replace the console credential used by <strong>{service.service_name}</strong>.
              Raven credentials are shared at organization level.
            </div>

            <Tabs value={source} onValueChange={(v) => setSource(v as Source)}>
              <TabsList>
                <TabsTrigger value="provision">Create on vEdge</TabsTrigger>
                <TabsTrigger value="service" disabled={siblings.length === 0}>
                  Copy from service
                </TabsTrigger>
                <TabsTrigger value="manual">Enter manually</TabsTrigger>
              </TabsList>

              <TabsContent value="provision">
                <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                  <Field label="Login email">
                    {(p) => (
                      <Input
                        {...p}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="off"
                      />
                    )}
                  </Field>

                  {checkPending && !account.isError && (
                    <Alert tone="info">Checking the email on vEdge…</Alert>
                  )}
                  {account.isError && (
                    <Alert tone="danger">Could not check the email on vEdge. Try again.</Alert>
                  )}
                  {!checkPending && acc && !acc.exists && (
                    <Alert tone="info">
                      No vEdge login for this email. It will be created as org_admin of the
                      selected vEdge organization with a generated password.
                    </Alert>
                  )}
                  {!checkPending && acc?.exists && !orgConflict && (
                    <Alert tone="warning">
                      This login already exists in vEdge org {acc.org_id}
                      {acc.org_name ? ` (${acc.org_name})` : ''}. Its password will be reset;
                      every Raven service using this login — in any org — gets the new one.
                    </Alert>
                  )}
                  {!checkPending && orgConflict && acc && (
                    <Alert tone="danger">
                      This login belongs to vEdge org {acc.org_id}
                      {acc.org_name ? ` (${acc.org_name})` : ''}. Pick that organization or use
                      another email — it will not be modified.
                    </Alert>
                  )}

                  {orgPicker}
                </div>
              </TabsContent>

              <TabsContent value="service">
                <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                  <Field label="Source Raven service">
                    {(p) => (
                      <Select value={fromId} onValueChange={setFromId}>
                        <SelectTrigger {...p}>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {siblings.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.service_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  {orgPicker}
                </div>
              </TabsContent>

              <TabsContent value="manual">
                <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                  <Field label="Console username">
                    {(p) => (
                      <Input
                        {...p}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoComplete="off"
                      />
                    )}
                  </Field>
                  <Field label="Console password">
                    {(p) => (
                      <Input
                        {...p}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    )}
                  </Field>
                  {orgPicker}
                </div>
              </TabsContent>
            </Tabs>

            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Checkbox checked={applyToOrg} onCheckedChange={(v) => setApplyToOrg(v === true)} />
              <span>
                Apply to all {orgCount} Raven service{orgCount === 1 ? '' : 's'} of this org
              </span>
            </label>

            <Field label="Reason (required)">
              {(p) => (
                <Input
                  {...p}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Rotate shared vncdn.vn account"
                  disabled={rebind.isPending}
                />
              )}
            </Field>

            {rebind.isError && (
              <Alert tone="danger">
                {errStatus?.status === 409 || errStatus?.status === 502
                  ? (errStatus.data?.error_message ?? 'vEdge rejected the request.')
                  : 'Failed to re-bind the credential. Please check the input and try again.'}
              </Alert>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={rebind.isPending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirm} disabled={!canSubmit}>
            {rebind.isPending ? 'Re-binding…' : source === 'provision' ? 'Create & bind' : 'Re-bind'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
